import React, { useContext, useEffect, useState } from "react";
import { LanguageSelectStyled } from "./LanguageSelectStyled";
import { useAppDispatch } from "../../../store/store";
import { fetchOperationalLanguages } from "../slice/action";
import { useSelector } from "react-redux";
import { getStateOfLanguage } from "../slice/selectors";
import { LocalStorageService } from "../../../services/LocalStorageService";
import {
  OperationalLanguageOptionInterface,
  LocalStorageLanguageProperty,
  TranslationContextInterface,
} from "../../../entities/Language";
import CustomSelect from "../../Select/CustomSelect";
import { TranslationContext } from "../../../context/TranslationContext";
import { getValueForKey } from "../../../context/translationsUtils";
import { TEST_ID } from "../../../shared/testid";
import { getTenantCredentials } from "../../Tenant/selectors";

const LanguageSelect: React.FC = () => {
  const { setContextLanguage, translations } =
    useContext<TranslationContextInterface>(TranslationContext);
  const credentials = useSelector(getTenantCredentials);
  const [languageOptions, setLanguageOptions] = useState<
    OperationalLanguageOptionInterface[] | []
  >([]);
  const [selectedLanguage, setSelectedLanguage] =
    useState<OperationalLanguageOptionInterface | null>(null);
  const { isLoading, data = [] } = useSelector(getStateOfLanguage);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchOperationalLanguages(""));
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && data.length) {
      const dataForOptions: OperationalLanguageOptionInterface[] = data.map(
        (item) => ({
          label: item.name,
          value: item.shortName,
          id: item.id.toString(),
        }),
      );
      setLanguageOptions(dataForOptions);
    }
  }, [data, isLoading]);

  useEffect(() => {
    const value = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE,
    );
    const id = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE_ID,
    );
    const label = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE_LABEL,
    );

    if (value && id && label) {
      setSelectedLanguage({
        value,
        id,
        label,
      });
    }
  }, []);

  useEffect(() => {
    const value = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE,
    );
    const id = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE_ID,
    );
    const label = LocalStorageService.getItem(
      LocalStorageLanguageProperty.LANGUAGE_LABEL,
    );

    if (!(value && id && label)) {
      const countryId = credentials?.country?.language?.id;
      const countryValue = credentials?.country?.language?.shortName;
      const countryLabel = credentials?.country?.language?.name;
      if (countryValue && countryId && countryLabel) {
        const idToString = countryId?.toString();
        setSelectedLanguage({
          value: countryValue,
          id: idToString,
          label: countryLabel,
        });
        LocalStorageService.setItem(
          LocalStorageLanguageProperty.LANGUAGE,
          countryValue,
        );
        LocalStorageService.setItem(
          LocalStorageLanguageProperty.LANGUAGE_ID,
          idToString,
        );
        LocalStorageService.setItem(
          LocalStorageLanguageProperty.LANGUAGE_LABEL,
          countryLabel,
        );
      }
    }
  }, [credentials?.country?.language]);

  const onHandleChange = (
    option: OperationalLanguageOptionInterface | null,
  ) => {
    if (option) {
      setSelectedLanguage(option);
      setContextLanguage(option.value);
      LocalStorageService.setItem(
        LocalStorageLanguageProperty.LANGUAGE,
        option.value,
      );
      LocalStorageService.setItem(
        LocalStorageLanguageProperty.LANGUAGE_ID,
        option.id,
      );
      LocalStorageService.setItem(
        LocalStorageLanguageProperty.LANGUAGE_LABEL,
        option.label,
      );
    }
  };

  const formatLanguageOptions = (
    option: OperationalLanguageOptionInterface,
  ) => (
    <div
      className="language-select__option"
      data-testid={`${
        TEST_ID.HEADER.SELECT_LANGUAGE_OPTION
      }-${option.value.toUpperCase()}`}
    >
      <div className="language-select__img">
        <img src={`/flags/${option.value.toLowerCase()}.svg`} alt="" />
      </div>
      <div className="language-select__label">{option.label}</div>
    </div>
  );

  return (
    <LanguageSelectStyled>
      <CustomSelect<OperationalLanguageOptionInterface>
        options={languageOptions}
        selectedOption={selectedLanguage}
        formatOptionLabel={formatLanguageOptions}
        onChange={(option) => onHandleChange(option)}
        placeholder={getValueForKey(translations, "nav_bar.language")}
        id={TEST_ID.HEADER.SELECT_LANGUAGE}
      />
    </LanguageSelectStyled>
  );
};
export default LanguageSelect;
